import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { StickyNote, Pencil, Save, X } from "lucide-react";

/**
 * Wraps a column header button so clicking it opens a small popover.
 * - Admins can edit the note and save.
 * - Regular users see the note (read-only) and are gently informed if it's empty.
 *
 * Props:
 *  - noteKey:  string  unique key for this column ("inv_delivery", "machine1", ...)
 *  - label:    React node — the visual content of the trigger button
 *  - note:     current note text for this key (may be empty)
 *  - isAdmin:  boolean
 *  - onSave:   async (key, value) => void   — only called for admins
 *  - className: passed to the trigger button
 */
export const ColumnNotePopover = ({ noteKey, label, note, isAdmin, onSave, className }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset draft to current note whenever the popover is opened — avoids stale state.
  const handleOpenChange = (next) => {
    if (next) setDraft(note || "");
    setOpen(next);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(noteKey, draft);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const hasNote = Boolean((note || "").trim());

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${className || ''} relative`}
          data-testid={`col-note-trigger-${noteKey}`}
          title={isAdmin ? "Click to edit column note" : (hasNote ? "Click to view note" : "Click for column note")}
        >
          {label}
          {hasNote && (
            <span
              className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.8)]"
              aria-hidden="true"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        className="w-72 p-3 bg-card border border-border shadow-xl"
        data-testid={`col-note-popover-${noteKey}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2">
          <StickyNote className="w-4 h-4 text-yellow-400" />
          <span className="font-mono text-sm font-bold text-card-foreground">Column Note</span>
        </div>
        {isAdmin ? (
          <>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write a short instruction users should see when they click this column…"
              rows={4}
              maxLength={500}
              className="font-mono text-xs"
              data-testid={`col-note-input-${noteKey}`}
              autoFocus
            />
            <div className="flex items-center justify-between mt-2 gap-2">
              <span className="text-[10px] text-muted-foreground">{draft.length}/500</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setDraft(note || ""); setOpen(false); }}
                  className="h-7 px-2"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="h-7 px-2 gap-1"
                  data-testid={`col-note-save-${noteKey}`}
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "..." : "Save"}
                </Button>
              </div>
            </div>
          </>
        ) : hasNote ? (
          <p className="text-sm text-card-foreground whitespace-pre-wrap font-mono leading-snug">
            {note}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground italic flex items-center gap-2">
            <Pencil className="w-3.5 h-3.5" />
            No note yet for this column.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};
